import React, { useCallback, useState } from 'react';
import { GoalId, GoalsFilterType, isGoalFilterType } from './types';
import { GoalSidebar } from './GoalSidebar/GoalSidebar';
import { useGoalFilter } from './useGoalFilter';
import { useSelectedGoal } from './useSelectedGoal';
import { GoalFilter } from './GoalsFilter/GoalsFilter';
import { GoalsListWithFilter } from './GoalsList/GoalsListWithFilter';
import { GoalPeriodSelector } from './GoalPeriodSelector/GoalPeriodSelector';
import { Col, Row, Tab, Tabs } from 'react-bootstrap';
import { t } from 'i18next';
import { useGoalsContext } from './GoalsContext/GoalsContext';
import { CREATE_GOAL_ID } from '../goalsLinks';
import Button from '../../../../../components/button/index';
import { PullGoalModal } from './PullGoalModal/PullGoalModal';

const i18prefix = 'performanceReview.goals2';

type Props = {};

export const GoalsPage: React.FC<Props> = () => {
  const { developmentFrameworkEnabled, isManagerView, currentPeriodId } =
    useGoalsContext();

  const [filter, setFilter] = useGoalFilter(
    isManagerView,
    !isManagerView ? 'myGoals' : 'myReportsGoals',
  );

  const { selectedGoalId, setSelectedGoalId } = useSelectedGoal();
  const [isPullGoalModalVisible, setIsPullGoalModalVisible] = useState(false);

  const handleFilterChange = useCallback(
    (filter: GoalFilter) => {
      setFilter(filter);
    },
    [setFilter],
  );

  const handleCloseSidebar = useCallback(
    () => setSelectedGoalId(null),
    [setSelectedGoalId],
  );

  const handleCreateGoal = useCallback(
    () => setSelectedGoalId(CREATE_GOAL_ID),
    [setSelectedGoalId],
  );

  const handleOpenPullGoalModal = useCallback(() => {
    setIsPullGoalModalVisible(true);
  }, []);

  const handleClosePullGoalModal = useCallback(() => {
    setIsPullGoalModalVisible(false);
  }, []);

  const handleGoalClick = useCallback(
    (goalId: GoalId) => {
      setSelectedGoalId(goalId);
    },
    [setSelectedGoalId],
  );

  const handleFilterTypeChange = useCallback(
    (type: string | null) => {
      if (!isGoalFilterType(type)) {
        return;
      }
      setFilter({ ...filter, filterType: type });
    },
    [filter, setFilter],
  );

  const filterTypes = getFilterTypes(isManagerView);

  return (
    <>
      <Row>
        <Col className="d-flex align-items-center justify-content-between ">
          <GoalPeriodSelector />
          {currentPeriodId && (
            <div className="d-flex gap-2 mt-3">
              {developmentFrameworkEnabled && (
                <Button
                  variant="outline-secondary"
                  onClick={handleOpenPullGoalModal}
                  label={t(`${i18prefix}.pullGoal.button`)}
                />
              )}
              <Button
                variant="primary"
                onClick={handleCreateGoal}
                label={t(`${i18prefix}.createGoal`)}
              />
            </div>
          )}
        </Col>
      </Row>
      <Row className="mb-3">
        <Col>
          <Tabs
            activeKey={filter.filterType}
            onSelect={handleFilterTypeChange}
            className="border-bottom-0">
            {filterTypes.map((filterType) => (
              <Tab
                key={filterType}
                eventKey={filterType}
                title={t(`${i18prefix}.filterType.${filterType}`)}
              />
            ))}
          </Tabs>
        </Col>
      </Row>
      <GoalsListWithFilter
        onCreateGoal={handleCreateGoal}
        onGoalClick={handleGoalClick}
        filter={filter}
        onFilterChange={handleFilterChange}
      />
      <GoalSidebar
        goalId={selectedGoalId !== CREATE_GOAL_ID ? selectedGoalId : null}
        visible={selectedGoalId !== null}
        onClose={handleCloseSidebar}
      />
      <PullGoalModal
        visible={isPullGoalModalVisible}
        onClose={handleClosePullGoalModal}
      />
    </>
  );
};

const getFilterTypes = (isManager: boolean): GoalsFilterType[] => {
  if (!isManager) {
    return ['myGoals', 'colleaguesGoals', 'allGoals'];
  } else {
    return ['myReportsGoals', 'allGoals'];
  }
};
